import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    useSyncExternalStore,
} from "react";
import { useAuth } from "../auth/AuthContext";
import { sessionEvents } from "../events/sessionEvents";
import { projectStore } from "./projectStore";
import {
    type AuthorizedProject,
    useAuthorizedProjects,
} from "./useAuthorizedProjects";

export type ProjectAccessState =
    | "no-jwt"
    | "loading"
    | "no-access"
    | "needs-pick"
    | "ready";

interface ProjectContextValue {
    /** Computed intersection of JWT.projectIds and /identity.projects[]. */
    authorizedProjects: AuthorizedProject[];
    /** UUID of the currently-selected project, or null. */
    selectedProjectId: string | null;
    /** Selects a project directly (no confirmation). Used for auto-select and the picker. */
    selectProject: (projectId: string) => void;
    /** Initiates a switch flow with confirmation. Same-project = no-op. */
    requestSwitch: (projectId: string) => void;
    /** True when a confirmation modal is pending. */
    pendingSwitch: { from: string; to: string } | null;
    /** Confirm the pending switch (cancels jobs + clears UI state via the bus, then mutates). */
    confirmSwitch: () => void;
    /** Abort the pending switch. */
    abortSwitch: () => void;
    /** High-level state used by `ProjectGate` to render the correct branch. */
    accessState: ProjectAccessState;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

export function ProjectProvider({ children }: { children: ReactNode }) {
    const { jwt } = useAuth();
    const { authorizedProjects, isLoading } = useAuthorizedProjects();
    const selectedProjectId = useSyncExternalStore(
        projectStore.subscribe,
        projectStore.get,
        () => null,
    );
    const [pendingSwitch, setPendingSwitch] = useState<
        { from: string; to: string } | null
    >(null);

    const selectProject = useCallback((projectId: string) => {
        projectStore.set(projectId);
    }, []);

    const requestSwitch = useCallback(
        (projectId: string) => {
            const current = projectStore.get();
            if (!current) {
                // No current selection — this is initial pick, not a switch.
                projectStore.set(projectId);
                return;
            }
            if (current === projectId) {
                // Same-project: no-op (PRD-004 DoD).
                return;
            }
            setPendingSwitch({ from: current, to: projectId });
        },
        [],
    );

    const confirmSwitch = useCallback(() => {
        if (!pendingSwitch) return;
        // Forward-compat seam for PRD-001's running-job cancellation:
        // subscribers run synchronously BEFORE the store mutates. PRD-001
        // will register `sessionEvents.on("project:switching", cancelMyJobs)`.
        sessionEvents.emit("project:switching", pendingSwitch);
        projectStore.set(pendingSwitch.to);
        sessionEvents.emit("project:switched", { projectId: pendingSwitch.to });
        setPendingSwitch(null);
    }, [pendingSwitch]);

    const abortSwitch = useCallback(() => {
        setPendingSwitch(null);
    }, []);

    // Auto-select-when-1: if exactly one authorized project exists and
    // none is currently selected, select it silently. PRD-004 DoD: no
    // picker shown for single-project users.
    useEffect(() => {
        if (selectedProjectId !== null) return;
        if (authorizedProjects.length !== 1) return;
        projectStore.set(authorizedProjects[0]!.id);
    }, [authorizedProjects, selectedProjectId]);

    // Race protection: if the JWT clears mid-switch (e.g. 401 path opens
    // the paste modal), close the confirmation modal so it isn't stranded.
    useEffect(() => {
        const unsub = sessionEvents.on("auth:logout", () => setPendingSwitch(null));
        return unsub;
    }, []);

    const accessState: ProjectAccessState = useMemo(() => {
        if (jwt === null) return "no-jwt";
        if (isLoading && selectedProjectId === null) {
            // jwt set but identity not yet ready → loading
            return "loading";
        }
        if (selectedProjectId !== null) return "ready";
        if (authorizedProjects.length === 0) return "no-access";
        return "needs-pick";
    }, [jwt, isLoading, selectedProjectId, authorizedProjects]);

    const value = useMemo<ProjectContextValue>(
        () => ({
            authorizedProjects,
            selectedProjectId,
            selectProject,
            requestSwitch,
            pendingSwitch,
            confirmSwitch,
            abortSwitch,
            accessState,
        }),
        [
            authorizedProjects,
            selectedProjectId,
            selectProject,
            requestSwitch,
            pendingSwitch,
            confirmSwitch,
            abortSwitch,
            accessState,
        ],
    );

    return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProject(): ProjectContextValue {
    const ctx = useContext(ProjectContext);
    if (!ctx) throw new Error("useProject must be used within a ProjectProvider");
    return ctx;
}
